import { FC, useEffect } from "react";
import { useAppStateContext } from "../context/state-context-provider";
import { UpdateUserWithServerResponse } from "../../helper/general/methods";
import { UserRegistration } from "../../core/database/user/user-registration";
import { UserAuthentication } from "../../core/database/user/user-authentication";
import { UserRetrieve } from "../../core/database/user/user-retrieve";
import { UserLogOut } from "../../core/database/user/user-logout";
import { UserPasswordRecover } from "../../core/database/user/user-password-recover";
import { UserProfileEdit } from "../../core/database/user/user-profile-edit";
import { UserExternalConnection } from "../../core/database/user/user-external-connection";
import { ProjectEdit } from "../../core/database/project/project-edit";
import { ProjectCreate } from "../../core/database/project/project-create";
import { ProjectDelete } from "../../core/database/project/project-delete";
import { ProjectDownload } from "../../core/database/project/project-download";
import { ProjectShare } from "../../core/database/project/project-share";
import { ModelRetrieve } from "../../core/database/file/model-retrieve";
import { ModelFragments } from "../../core/database/file/model-fragments";
import { PdfRetrieve } from "../../core/database/file/pdf-retrieve";
import { FileDownload } from "../../core/database/file/file-download";
import { FileDelete } from "../../core/database/file/file-delete";
import { FileUpload } from "../../core/database/file/file-upload";
import { ForgeCreate } from "../../core/database/forge/forge-create";
import { ForgeEdit } from "../../core/database/forge/forge-edit";
import { ForgeRun } from "../../core/database/forge/forge-run";
import { ForgeDelete } from "../../core/database/forge/forge-delete";
import { FolderCreate } from "../../core/database/folder/folder-create";
import { FolderDelete } from "../../core/database/folder/folder-delete";
import { FolderDownload } from "../../core/database/folder/folder-download";
import { FolderEdit } from "../../core/database/folder/folder-edit";
import { ReportDelete } from "../../core/database/report/report-delete";
import { ReportDownload } from "../../core/database/report/report-download";
import { ReportRetrieve } from "../../core/database/report/report-retrieve";
import { ReportDescriptionRetrieve } from "../../core/database/report/report-description-retrieve";
import { ReportDescriptionUpload } from "../../core/database/report/report-description-upload";
import { ReportElementNoteUpload } from "../../core/database/report/report-elemment-note-upload";
import {
  CreateReportFromServerResponse,
  UpdateReportWithServerResponse,
} from "../../helper/report/methods";
import { ReportElementNoteEdit } from "../../core/database/report/report-elemment-note-edit";
import { ReportElementNoteDelete } from "../../core/database/report/report-element-note.delete";
import { ServerResponseInterface } from "../interfaces/server/server-response-interface";
import { UserSubscriptionRetrieve } from "../../core/database/user/user-subscription-retrieve";
import { ProjectTeamRetrieve } from "../../core/database/project/project-team-retrieve";
import { ProjectTeamEdit } from "../../core/database/project/project-team-edit";

export const CoreHandler: FC = () => {
  const [state, dispatch] = useAppStateContext();

  useEffect(() => {
    if (state.data) {
      const postCall = async (operation: any) => {
        dispatch({
          type: "RESPONSE",
          response: "WAIT",
        });

        let serverResponse;
        try {
          switch (operation) {
            // USER SECTION
            case "UserRegistrationInterface":
              serverResponse = await UserRegistration.Register(state);
              break;
            case "UserAuthenticationWebInterface":
              serverResponse = await UserAuthentication.Authenticate(state);
              break;
            case "UserProfileEditInterface":
              serverResponse = await UserProfileEdit.Edit(state);
              break;
            case "UserExternalConnectionInterface":
              serverResponse = await UserExternalConnection.Create(state);
              break;
            case "UserPasswordRecoverInterface":
              serverResponse = await UserPasswordRecover.Recover(state);
              break;
            case "UserRetrieveInterface":
              serverResponse = await UserRetrieve.Retrieve(state);
              break;
            case "UserSubscriptionRetrieveInterface":
              serverResponse = await UserSubscriptionRetrieve.Retrieve(state);
              break;
            case "UserLogOutInterface":
              serverResponse = await UserLogOut.LogOut(state);
              break;
            // PROJECT SECTION
            case "ProjectInterface":
              serverResponse = await ProjectCreate.Create(state);
              break;
            case "ProjectDeleteInterface":
              serverResponse = await ProjectDelete.Delete(state);
              break;
            case "ProjectDownloadInterface":
              serverResponse = await ProjectDownload.Download(state, dispatch);
              break;
            case "ProjectShareInterface":
              serverResponse = await ProjectShare.Share(state);
              break;
            case "ProjectEditInterface":
              serverResponse = await ProjectEdit.Edit(state);
              break;
            case "ProjectTeamRetrieveInterface":
              serverResponse = await ProjectTeamRetrieve.Retrieve(state);
              break;
            case "ProjectTeamEditInterface":
              serverResponse = await ProjectTeamEdit.Edit(state);
              break;
            // FORGE SECTION
            case "ForgeCreateInterface":
              serverResponse = await ForgeCreate.Create(state);
              break;
            case "ForgeEditInterface":
              serverResponse = await ForgeEdit.Edit(state);
              break;
            case "ForgeDeleteInterface":
              serverResponse = await ForgeDelete.Delete(state);
              break;
            case "ForgeRunInterface":
              serverResponse = await ForgeRun.Run(state);
              break;
            // FOLDER SECTION
            case "FolderCreateInterface":
              serverResponse = await FolderCreate.Create(state);
              break;
            case "FolderDeleteInterface":
              serverResponse = await FolderDelete.Delete(state);
              break;
            case "FolderEditInterface":
              serverResponse = await FolderEdit.Edit(state);
              break;
            case "FolderDownloadInterface":
              serverResponse = await FolderDownload.Download(state, dispatch);
              break;
            // FILE AND MODEL SECTION
            case "FileUploadInterface":
            case "ModelUploadInterface":
              serverResponse = await FileUpload.Upload(state, dispatch);
              break;
            case "FileDeleteInterface":
              serverResponse = await FileDelete.Delete(state);
              break;
            case "FileDownloadInterface":
              serverResponse = await FileDownload.Download(state);
              break;
            case "ModelRetrieveInterface":
              serverResponse = await ModelRetrieve.Retrieve(state);
              break;
            case "ModelFragmentsInterface":
              serverResponse = await ModelFragments.Store(state);
              break;
            case "PdfRetrieveInterface":
              serverResponse = await PdfRetrieve.Retrieve(state);
              break;
            // REPORT SECTION
            case "ReportDeleteInterface":
              serverResponse = await ReportDelete.Delete(state);
              break;
            case "ReportDownloadInterface":
              serverResponse = await ReportDownload.Download(state, dispatch);
              break;
            case "ReportRetrieveInterface":
              serverResponse = await ReportRetrieve.Retrieve(state);
              break;
            case "ReportDescriptionRetrieveInterface":
              serverResponse = await ReportDescriptionRetrieve.Retrieve(state);
              break;
            case "ReportDescriptionUploadInterface":
              serverResponse = await ReportDescriptionUpload.Upload(state);
              break;
            case "ReportElementNoteUploadInterface":
              serverResponse = await ReportElementNoteUpload.Upload(state);
              break;
            case "ReportElementNoteEditInterface":
              serverResponse = await ReportElementNoteEdit.Edit(state);
              break;
            case "ReportElementNoteDeleteInterface":
              serverResponse = await ReportElementNoteDelete.Delete(state);
              break;
            default:
              throw new Error("Unknown operation type");
          }

          dispatch({
            type: "CLEAR_DATA",
          });

          if (serverResponse != null) {
            if (typeof serverResponse === "string") {
              dispatch({
                type: "RESPONSE",
                response: serverResponse,
              });
            } else if (typeof serverResponse === "object") {
              const serverResponseObject =
                serverResponse as ServerResponseInterface;

              if (serverResponseObject.status === "error") {
                {
                  dispatch({
                    type: "RESPONSE",
                    response: serverResponseObject.message,
                  });
                }
              } else if (serverResponseObject.header === "url") {
                dispatch({
                  type: "OPEN_URL",
                  payload: serverResponseObject.message,
                  response: "",
                });
              } else if (operation === "ModelRetrieveInterface") {
                dispatch({
                  type: "OPEN_MODEL",
                  payload: serverResponse,
                  response: "",
                });
              } else if (operation === "PdfRetrieveInterface") {
                dispatch({
                  type: "OPEN_PDF",
                  payload: serverResponse,
                  response: "",
                });
              } else if (operation === "ModelFragmentsInterface") {
                // Re-opens the model the way an already-processed one arrives,
                // so the viewport takes its ordinary fragments path from here.
                dispatch({
                  type: "OPEN_MODEL",
                  payload: serverResponseObject.data,
                  response: "",
                });
              } else if (operation === "ReportRetrieveInterface") {
                dispatch({
                  type: "OPEN_REPORT",
                  payload: CreateReportFromServerResponse(serverResponseObject),
                  response: "",
                });
              } else if (operation === "ReportDescriptionRetrieveInterface") {
                dispatch({
                  type: "OPEN_REPORT_DESCRIPTION",
                  payload: serverResponseObject.data!,
                });
              } else if (
                operation === "ReportElementNoteUploadInterface" ||
                operation === "ReportElementNoteDeleteInterface" ||
                operation === "ReportElementNoteEditInterface"
              ) {
                const updatedReport = UpdateReportWithServerResponse(
                  state.openReport!,
                  serverResponseObject.data!,
                );
                dispatch({
                  type: "OPEN_REPORT",
                  payload: updatedReport,
                  response: "",
                });
              } else if (operation === "ProjectTeamRetrieveInterface") {
                dispatch({
                  type: "OPEN_PROJECT_TEAM",
                  payload: serverResponseObject.data!,
                  response: "",
                });
              } else {
                UpdateUserWithServerResponse(
                  serverResponseObject,
                  dispatch,
                  state.user!,
                );
              }
            }
          } else {
            console.warn("Server response is null ==> ", operation);
          }
        } catch (error) {
          dispatch({
            type: "RESPONSE",
            response:
              (error as Error).message ||
              "An unexpected error occurred while processing your request.",
          });
        }
      };

      postCall(state.data.type);
    }
  }, [state.data]);

  return <></>;
};
